import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { Logo, LangToggle } from "@/components/SiteHeader";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { formatApiErrorDetail } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1712873068978-eb0cb20d1b62?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Auth({ mode = "login" }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", password: "",
    phone: "", country: "United States", dial_code: "+1",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = isLogin ? await login(form.email, form.password) : await register(form);
      toast.success(isLogin ? "Welcome back." : "Account created.");
      navigate(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen grid lg:grid-cols-2">
      <div className="clu-ambient" />
      {/* Left visual */}
      <div className="relative hidden lg:block border-r border-white/10 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-40" style={{ backgroundImage: `url(${HERO_IMG})` }} />
        <div className="absolute inset-0 bg-gradient-to-br from-sapphire/80 via-sapphire/70 to-sapphire" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <Logo />
          <div>
            <h2 className="font-display text-5xl font-extrabold tracking-tight leading-[1] mb-4">
              {t("auth.leftHeadline1")} <span className="text-gold">{t("auth.leftHeadline2")}</span>{t("auth.leftHeadline3")}
            </h2>
            <p className="text-white/60 max-w-md">{t("auth.leftSub")}</p>
          </div>
          <div className="font-mono text-xs text-white/40 uppercase tracking-[0.2em]">{t("footer.compliance")}</div>
        </div>
      </div>

      {/* Right form */}
      <div className="relative z-10 flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
          <div className="flex items-center justify-between mb-8">
            <div className="lg:hidden"><Logo /></div>
            <div className="ml-auto"><LangToggle /></div>
          </div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight mb-2">
            {isLogin ? t("auth.signIn") : t("auth.create")}
          </h1>
          <p className="text-white/50 mb-8 text-sm">
            {isLogin ? t("auth.signInSub") : t("auth.createSub")}
          </p>

          <form className="space-y-4" onSubmit={onSubmit}>
            {!isLogin && (
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("f.firstName")}><input data-testid="auth-first-name" required value={form.first_name} onChange={set("first_name")} className="clu-input" /></Field>
                <Field label={t("f.lastName")}><input data-testid="auth-last-name" required value={form.last_name} onChange={set("last_name")} className="clu-input" /></Field>
              </div>
            )}
            <Field label={t("f.email")}><input data-testid="auth-email" required type="email" value={form.email} onChange={set("email")} className="clu-input" /></Field>
            {!isLogin && (
              <div className="grid grid-cols-[110px_1fr] gap-2">
                <Field label={t("f.code")}><input data-testid="auth-dial" value={form.dial_code} onChange={set("dial_code")} className="clu-input" /></Field>
                <Field label={t("f.phone")}><input data-testid="auth-phone" value={form.phone} onChange={set("phone")} className="clu-input" /></Field>
              </div>
            )}
            <Field label={t("f.password")}><input data-testid="auth-password" required type="password" value={form.password} onChange={set("password")} className="clu-input" /></Field>
            <button type="submit" data-testid="auth-submit-btn" disabled={loading} className="w-full bg-gold hover:bg-gold-soft disabled:opacity-50 text-sapphire font-semibold py-3 rounded-md flex items-center justify-center gap-2 transition-colors">
              {loading ? t("auth.wait") : isLogin ? t("auth.signInBtn") : t("auth.createBtn")} <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <p className="text-sm text-white/50 text-center pt-6">
            {isLogin ? (
              <>{t("auth.newHere")} <Link data-testid="auth-switch-register" to="/register" className="text-gold hover:underline">{t("auth.createLink")}</Link></>
            ) : (
              <>{t("auth.alreadyClient")} <Link data-testid="auth-switch-login" to="/login" className="text-gold hover:underline">{t("auth.signInLink")}</Link></>
            )}
          </p>
          <p className="text-center pt-3">
            <Link to="/" className="text-xs font-mono uppercase tracking-[0.2em] text-white/40 hover:text-white/70">{t("auth.back")}</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.15em] text-white/60 font-mono block mb-2">{label}</span>
      {children}
    </label>
  );
}
